import { useTranslation } from "react-i18next";
import { Page } from "components";
import serverSideTranslations from "utils/serverSideTranslations";
import { GetStaticPathsContext, GetStaticPropsContext } from "next";
import
  {
    getCountriesWithPublishedTrips,
  } from "utils/api/site2";

import { getDestinationPage } from "utils/api/cms";
import { useAddToDataLayer } from "utils/hooks/use-add-to-datalayer";
import { currentEnvironment } from "utils/current-environment";
import { currentMarket } from "utils/current-market";
import { translateRoute } from "utils/localisedRouteHelpers";
import styles from "../country.module.scss";
import { isLocale, Locale } from "@travellocal/utils";
import yearsForCountryTitle from "utils/yearsForCountryTitle";

import { Country, CountryProps, getStaticPropsForCountry } from "../../../components/Destinations/Country";
import { CountryV2, CountryV2Props, getStaticPropsForCountryV2 } from "../../../components/Destinations/CountryV2";

export type PageProps = {
  version: 'v1' | 'v2';
  data: CountryProps | CountryV2Props;
};

function PageWrapper({
  version,
  data,
}:
  PageProps
)
{
  const { t } = useTranslation();
  const pageTitle = t("common:pageName_country", "Visit {{country}} {{years}}", {
    country: data.country?.name,
    years: yearsForCountryTitle(),
  });

  useAddToDataLayer({
    environment: currentEnvironment(), // use this with: prod, staging, dev
    page: {
      market: currentMarket(data.currentLocale), // NEW, I'd like to introduce this (de, en, en_GB, NOTE, NOT "en-gb")
      destinationContinent: data.country?.continent?.internalKey, // please use this (obtained by site 2 API)
      destinationCountry: data.country?.internalKey, // please use this
      language: data.currentLocale, // please use this (duplicate of market)
      subtype: "country lp", // please use this, it is static text
      type: "category", // please use this, it is static text
    },
  });

  const canonicalUrl = translateRoute(data.currentLocale, `destinationsByCountry`, {
    country: data.country?.slug || data.countryParam,
  });

  return (
    <Page
      title={pageTitle}
      alternatePages={data.alternatePages}
      meta={{
        title: data.meta.searchTitle,
        description: data.meta.searchDescription,
        imageSrc: data.meta.searchImage?.sourceUrl,
        locale: data.currentLocale,
        canonicalUrl,
      }}
      {...data.pageProps}>
      {version === 'v2' ? (
        <CountryV2 styles={styles} {...(data as CountryV2Props)} />
      ) : (
        <Country styles={styles} {...(data as CountryProps)} />
      )}
    </Page>
  );

}

export const getStaticProps = async (context: GetStaticPropsContext) =>
{

  const countryParam = Array.isArray(context.params.country)
    ? context.params.country[0].toLowerCase()
    : context.params.country.toLowerCase();

  const destinationData = await getDestinationPage(countryParam, [context.locale as Locale], "getDestinationPage");

  if (!destinationData) {
    return {
      notFound: true,
    };
  }
  const sst = await serverSideTranslations(context.locale as Locale)

  if (destinationData?.country?.destinationPageVersion == "v1") {
    const data = await getStaticPropsForCountry(context)
    return {
      props: {
        version: "v1",
        data: { ...data.props },
        ...sst
      }
    };
  } else {
    const data = await getStaticPropsForCountryV2(context);
    return {
      props: {
        version: "v2",
        data: { ...data.props },
        ...sst
      }
    };
  }

};

export async function getStaticPaths({ locales = [] }: GetStaticPathsContext)
{
  // initialize country slug array
  const countryPaths = [];

  /* get all available countries for each locale  */
  for (const locale of locales) {
    if (!isLocale(locale)) {
      continue;
    }

    const countries = await getCountriesWithPublishedTrips(locale);

    // add country slugs to array
    countries.forEach((country) =>
    {
      countryPaths.push({
        params: { country: country.slug },
        locale: locale,
      });
    });
  }

  // We'll pre-render only these paths at build time.
  // {fallback: 'blocking' } will server-render pages
  // on-demand if the path doesn't exist.
  return { paths: countryPaths, fallback: "blocking" };
}

export default PageWrapper;
